import { useMemo, useState } from "react";
import Seo from "@/components/Seo";
import { PageHero, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GALLERY_CATEGORIES, GALLERY_ITEMS } from "@/data/site";

export default function Gallery() {
  const [category, setCategory] = useState("All");
  const [selected, setSelected] = useState(null);

  const items = useMemo(
    () => (category === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === category)),
    [category]
  );

  return (
    <>
      <Seo
        title="Gallery — BNM Sustainable Energy Limited"
        description="Media gallery of BNM Sustainable Energy Limited: the Carbon Free Generator, technology, innovation and clean-energy imagery from Sierra Leone."
      />
      <PageHero
        testid="gallery-hero"
        eyebrow="Gallery"
        lines={["Technology,", "in view."]}
        lead="Media from BNM's development work. Where company photography is not yet available, clearly-labelled representative industry imagery is used — never presented as BNM activity."
      />

      <section data-testid="gallery-section" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Gallery categories">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={category === c}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  category === c
                    ? "border-obsidian bg-obsidian text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-emerald-400 hover:text-emerald-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <div className="mt-12 rounded-lg border border-dashed border-slate-300 bg-white/60 p-14 text-center">
              <p className="font-display text-xl font-bold text-slate-700">Photography coming soon</p>
              <p className="mt-3 text-sm text-slate-500">
                BNM will publish {category.toLowerCase()} photography here as it becomes available.
              </p>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {items.map((item, i) => (
                <Reveal key={item.title} delay={(i % 3) * 0.07}>
                  <figure className="group rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <button
                      data-testid={`gallery-item-${i}`}
                      onClick={() => setSelected(item)}
                      aria-label={`View ${item.title}`}
                      className="block w-full text-left"
                    >
                      <span className="block overflow-hidden">
                        <img
                          src={item.src}
                          alt={item.title}
                          loading="lazy"
                          width="1200"
                          height="800"
                          className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </span>
                    </button>
                    <figcaption className="p-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-600">{item.category}</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{item.title}</p>
                      <p className="mt-1 text-xs text-slate-500">{item.caption}</p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl bg-white" data-testid="gallery-lightbox">
          {selected && (
            <>
              <DialogTitle className="font-display text-lg font-bold text-slate-900">{selected.title}</DialogTitle>
              <img
                src={selected.src}
                alt={selected.title}
                className="w-full max-h-[60vh] rounded-md object-cover"
              />
              <DialogDescription className="text-sm text-slate-500">{selected.caption}</DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CTABand
        title="Want to see the technology in person?"
        text="Institutional and media visits to BNM in Waterloo, Sierra Leone can be arranged through the contact page."
        primary={{ label: "Arrange a Visit", to: "/contact", testid: "gallery-contact-cta" }}
        testid="gallery-cta"
      />
    </>
  );
}
